import React from 'react'
import Error from 'next/error'

import { generateHref } from 'api/utils'
import {
  fetchStaticPageProps,
  StaticPageNotFoundError,
} from 'utils/PaginatedPageProps'

import Layout from 'layouts/default'
import SectionBuilder from '@/organisms/SectionBuilder'

function parsePageNumber(value) {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  if (!normalizedValue || !/^\d+$/.test(normalizedValue)) return null

  return Number(normalizedValue)
}

function getTotalPages(sections) {
  const gridSection = sections.find((section) => (
    section.type === 'Grid' &&
    Number(section.data?.perPage) > 0 &&
    Number(section.data?.totalItems) > 0
  ))

  if (!gridSection) return 1

  return Math.ceil(
    Number(gridSection.data.totalItems) / Number(gridSection.data.perPage)
  )
}

export async function getServerSideProps({
  params,
  query,
  res,
}) {
  const pageNumber = parsePageNumber(query.page)
  const basePath = generateHref(params.slug)

  if (!pageNumber || pageNumber <= 1) {
    return {
      redirect: {
        destination: basePath,
        permanent: true,
      }
    }
  }

  let staticPageProps
  try {
    staticPageProps = await fetchStaticPageProps(basePath)
  } catch (error) {
    if (
      error instanceof StaticPageNotFoundError ||
      error?.code === 'STATIC_PAGE_NOT_FOUND'
    ) {
      return { notFound: true }
    }

    res.statusCode = 503
    res.setHeader('Cache-Control', 'no-store')
    res.setHeader('Retry-After', '60')
    res.setHeader('X-Pagination-Error', 'static-props-unavailable')

    return {
      props: {
        serviceUnavailable: true,
      }
    }
  }

  const totalPages = getTotalPages(staticPageProps.sections || [])

  if (pageNumber > totalPages) return { notFound: true }

  const props = {
    ...staticPageProps,
    metadata: {
      ...staticPageProps.metadata,
      paginationPage: pageNumber,
    }
  }

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=86400'
  )

  return { props }
}

export default function PaginatedPage({
  sections,
  metadata,
  additionalData,
  serviceUnavailable = false,
}) {
  if (serviceUnavailable) return <Error statusCode={503} />

  return (
    <Layout metadata={metadata}>
      <SectionBuilder data={sections} additionalData={additionalData} />
    </Layout>
  )
}
